/** Stope u trenutku bookinga. Snimaju se na seansu da kasnija promjena ne pomjeri historiju. */
export interface MoneyConfig {
  /** Udio, ne procetat: 0.17 za 17%. */
  vatRate: number;
  /** Procetat, ne udio: 20 za 20%. */
  feePercent: number;
}

export interface TherapistTaxProfile {
  payoutMethod: "invoice" | "work_contract";
  /** `businessVatId` popunjen i neprazan. */
  isVatRegistered: boolean;
}

export interface SessionMoney {
  /** Šta klijent plaća, sa PDV-om. Iznos na fiskalnom računu. */
  clientTotal: number;
  vatRate: number;
  /** Izlazni PDV Psihotrona. */
  vat: number;
  /** Neto prihod Psihotrona, osnovica bez PDV-a. */
  platformBase: number;
  feePercent: number;
  /** Marža Psihotrona, neto. */
  platformFee: number;
  /** Trošak Psihotrona, neto. Kod ugovora o djelu je to bruto naknada. */
  therapistNet: number;
  /** Odbitni ulazni PDV sa terapeutove fakture, 0 ako terapeut nije obveznik. */
  therapistVat: number;
  /** Iznos koji se stvarno isplaćuje. Kod ugovora o djelu prije obustava. */
  payoutGross: number;
}

export interface SessionMoneyOptions {
  /**
   * Bruto cijena prije rabata firme. Kad je zadana, terapeutov dio se računa
   * od nje, a rabat (razlika do `clientTotal`) ide samo iz provizije. Pozivalac
   * mora rabat ograničiti na stopu provizije (`resolveCompanyFunding`).
   */
  listPrice?: number;
}

export function computeSessionMoney(
  clientTotal: number,
  config: MoneyConfig,
  tax: TherapistTaxProfile,
  options: SessionMoneyOptions = {}
): SessionMoney {
  const total = round2(Math.max(0, clientTotal));
  const platformBase = round2(total / (1 + config.vatRate));
  const vat = round2(total - platformBase);
  const listTotal = round2(Math.max(total, options.listPrice ?? total));
  const listBase = round2(listTotal / (1 + config.vatRate));
  // Rabat jednak cijeloj proviziji se zbog zaokruživanja može razići za fening;
  // tada platforma ostaje na nuli, terapeut ne dobija više od cijele osnovice.
  const therapistNet = Math.min(platformBase, round2(listBase * (1 - config.feePercent / 100)));
  const platformFee = round2(platformBase - therapistNet);
  const { therapistVat, payoutGross } = computeTherapistPayoutGross(therapistNet, config.vatRate, tax);

  return {
    clientTotal: total,
    vatRate: config.vatRate,
    vat,
    platformBase,
    feePercent: config.feePercent,
    platformFee,
    therapistNet,
    therapistVat,
    payoutGross,
  };
}

/**
 * Terapeutov PDV i bruto za isplatu iz snimljenog neto dijela i stope. Jedino
 * pravilo za taj dio: koristi ga `computeSessionMoney` pri bookingu i
 * `resnapshotUnpaidTherapistTax` kad terapeut promijeni poreski status.
 */
export function computeTherapistPayoutGross(
  therapistNet: number,
  vatRate: number,
  tax: TherapistTaxProfile
): { therapistVat: number; payoutGross: number } {
  // Ugovor o djelu je odnos sa fizičkim licem, tu fakture s PDV-om nema ni kad
  // terapeut ima obrt registrovan za PDV po nekom drugom osnovu.
  const therapistVat =
    tax.payoutMethod === "invoice" && tax.isVatRegistered ? round2(therapistNet * vatRate) : 0;
  return { therapistVat, payoutGross: round2(therapistNet + therapistVat) };
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
